<template>
    <StatisticsLayout :title="$t('statistics.mode_tactics')" :is-empty="totalGames === 0">
        <template #summary>
            <div class="grid grid-cols-2 gap-x-4">
                <StatsHeroTile
                    :title="$t('statistics.total_games')"
                    :value="totalGames"
                    :footnote="
                        $t('statistics.vs_last_month', {
                            delta: statisticsStore.gamesVsLastMonth('matchtype_tactics'),
                        })
                    "
                />
                <StatsHeroTile
                    variant="light"
                    :title="$t('statistics.daily_streak')"
                    :value="modeStats.daily_streak?.current ?? 0"
                    :footnote="
                        $t('statistics.highest', { count: modeStats.daily_streak?.highest ?? 0 })
                    "
                />
            </div>
        </template>

        <WinrateCard v-bind="modeStats.winrate" />

        <Panel size="full" padding="p-4" :corners="false">
            <div class="flex items-center justify-between gap-4">
                <h2 class="text-base leading-6 font-black tracking-[0.25px] uppercase">
                    {{ $t('statistics.average_mpr') }}
                </h2>
                <button
                    type="button"
                    class="shrink-0"
                    :aria-label="$t('statistics.mpr_info_title')"
                    data-testid="mpr-info-button"
                    @click="showMprInfo = true"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M7.50059 4.21606C13.4983 0.758474 21 5.07897 21 12.0003C21 15.2121 19.284 18.1793 16.4994 19.7846L16.4994 19.7846C10.5017 23.2422 3 18.9217 3 12.0003C3 8.78849 4.71602 5.82135 7.50058 4.21607L7.50059 4.21606ZM19 12.0003C19 6.62318 13.1684 3.25723 8.49945 5.94876L8.00002 5.08241L8.49945 5.94876C6.33348 7.19744 5 9.50447 5 12.0003C5 17.3775 10.8316 20.7434 15.5005 18.0519C17.6665 16.8032 19 14.4962 19 12.0003ZM13 7.00032L13 8.50032L11 8.50032L11 7.00032L13 7.00032ZM13 10.0003V17.0003H11V10.0003H13Z"
                            fill="white"
                        />
                    </svg>
                </button>
            </div>
            <div class="border-stroke-1 mt-6 border-b pb-6">
                <span class="font-title text-[30px] leading-[30px] font-extrabold">
                    {{ formatMpr(modeStats.mpr?.overall) }}
                </span>
            </div>
            <dl class="mt-6 flex flex-col gap-4 text-sm leading-5 tracking-[0.25px]">
                <div class="flex items-center justify-between">
                    <dt>{{ $t('statistics.mpr_cricket') }}</dt>
                    <dd class="font-black">{{ formatMpr(modeStats.mpr?.cricket) }}</dd>
                </div>
                <div class="flex items-center justify-between">
                    <dt>{{ $t('statistics.mpr_tactics') }}</dt>
                    <dd class="font-black">{{ formatMpr(modeStats.mpr?.tactics) }}</dd>
                </div>
            </dl>
        </Panel>
        <PersonalRecords :records="records" />

        <BottomSheet :open="showMprInfo" @close="showMprInfo = false">
            <template #title>{{ $t('statistics.mpr_info_title') }}</template>
            <div class="flex flex-col gap-6 text-sm" data-testid="mpr-info">
                <p>{{ $t('statistics.mpr_explanation') }}</p>
                <div>
                    <h4 class="leading-5 font-black uppercase">{{ $t('statistics.example') }}</h4>
                    <p>
                        {{ $t('statistics.mpr_example') }}
                        <b class="font-black">(20 / 50) x 3 = 1.2</b>
                    </p>
                </div>
            </div>
        </BottomSheet>
    </StatisticsLayout>
</template>

<script>
import { mapStores } from 'pinia'
import { useStatisticsStore } from '@/stores/statistics.js'
import StatisticsLayout from '@/components/statistics/StatisticsLayout.vue'
import StatsHeroTile from '@/components/statistics/StatsHeroTile.vue'
import Panel from '@/components/layout/Panel.vue'
import WinrateCard from '@/components/statistics/WinrateCard.vue'
import PersonalRecords from '@/components/statistics/PersonalRecords.vue'
import BottomSheet from '@/components/ui/BottomSheet.vue'

export default {
    name: 'TacticsStatsView',
    components: {
        StatisticsLayout,
        StatsHeroTile,
        Panel,
        BottomSheet,
        WinrateCard,
        PersonalRecords,
    },
    data() {
        return {
            showMprInfo: false,
        }
    },
    computed: {
        records() {
            return (this.modeStats.mpr?.records ?? []).map((record) => ({
                title: this.$t('statistics.record_mpr', { value: record.value.toFixed(2) }),
                details: [
                    record.variant === 'tactics'
                        ? this.$t('statistics.variant_tactics')
                        : this.$t('statistics.variant_cricket'),
                    record.win_type === 'best_of'
                        ? this.$t('match_settings.best_of')
                        : this.$t('match_settings.first_to'),
                ],
                playedAt: record.played_at,
            }))
        },

        ...mapStores(useStatisticsStore),

        totalGames() {
            return this.statisticsStore.totalGames('matchtype_tactics')
        },

        modeStats() {
            return this.statisticsStore.allTime?.mode_stats?.matchtype_tactics ?? {}
        },
    },
    methods: {
        formatMpr(value) {
            return value == null ? '-' : value.toFixed(2)
        },
    },
}
</script>
